import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AssetDTO, CreateInspectionRequestDTO, CreateInspectionResponseDTO, FormElementKind, FormType } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { match } from 'ts-pattern';

import { SaveChangesDialog } from '@/components/common/save-changes-dialog';
import { initializeTableInitialValues } from '@/components/form/elements/table';
import { FormElementsRenderer, FormRendererData } from '@/components/form/form-elements-renderer';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import { Sheet, SheetContent, SheetHeader } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { getFormByIdQueryOptions } from '@/queries/forms';
import { activityKeys, inspectionKeys } from '@/queries/inspections';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export default function InspectionFormDrawer({
  formId,
  assetId,
  open,
  onOpenChange,
  onSuccess,
}: {
  formId?: string;
  assetId: string;
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  onSuccess?: (response: CreateInspectionResponseDTO) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isConfirmSaveDirtyChangesOpen, setIsConfirmSaveDirtyChangesOpen] = useState(false);
  const [isSheetAnimationComplete, setIsSheetAnimationComplete] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetDTO>();
  const formDataUpdatedAt = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      formDataUpdatedAt.current = new Date().getTime();
    }
  }, [open]);

  const { data: asset, isLoading: assetLoading } = useQuery({ ...getAssetByIdQueryOptions({ assetId, isAWA: true }), enabled: !!assetId && open });

  const { data: formData, isLoading: formLoading } = useQuery({ ...getFormByIdQueryOptions({ formId, isAWA: true }), enabled: !!formId && open });
  const form = formData?.form ?? null;

  useEffect(() => {
    if (asset && !assetLoading) {
      setSelectedAsset(asset);
    }
  }, [asset, assetLoading]);

  const defaultFormValues = useMemo(() => {
    if (!form) return {};

    return form.elements.reduce(
      (acc, element) => ({
        ...acc,
        ...(element.kind === FormElementKind.Table
          ? {
              [element.id]: [[...initializeTableInitialValues(element)]],
            }
          : {
              [element.id]: undefined,
            }),
      }),
      {} as FormRendererData,
    );
  }, [form]);

  const formInstance = useForm<FormRendererData>({
    defaultValues: defaultFormValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open) {
      formInstance.reset(defaultFormValues);
      setIsFormDirty(false);
    }
  }, [open, defaultFormValues, formInstance]);

  useEffect(() => {
    if (formInstance.formState.isDirty) {
      setIsFormDirty(formInstance.formState.isDirty);
    }
  }, [formInstance.formState.isDirty]);

  const { mutateAsync: createInspectionForm, isPending: isCreatingInspection } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!form?.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      const createInspectionResponse = await (
        await AWAApiV2Service.CreateInspectionForm({
          assetId: assetId,
          startedAt: dayjs(formDataUpdatedAt.current).toISOString(),
          formRevisionId: form.latestRevisionId,
          data: Object.entries(data).map(([key, value]) => ({
            elementId: key,
            value: value === undefined || value === '' ? null : value,
            kind: form.elements.find((element) => element.id === key)!.kind as CreateInspectionRequestDTO['data'][number]['kind'],
          })),
        })
      ).json();

      return createInspectionResponse;
    },
    onError: () => {
      toast.error('Unable to save changes. Please try again.');
    },
    onSuccess: (createInspectionResponse) => {
      toast.success('Record created');
      queryClient.invalidateQueries({ queryKey: inspectionKeys.latestByAsset(assetId) });
      queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(assetId) });
      queryClient.invalidateQueries({ queryKey: form?.id ? inspectionKeys.byAssetForm(assetId, form.id) : inspectionKeys.all });
      onSuccess?.(createInspectionResponse);
      onOpenChange?.(false, true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const { mutateAsync: createRepairForm, isPending: isCreatingRepair } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!form?.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      const response = await AWAApiV2Service.CreateRepairForm({
        assetId: assetId,
        formRevisionId: form.latestRevisionId,
        data: Object.entries(data).map(([key, value]) => ({
          elementId: key,
          value: value === undefined ? null : value,
          kind: form.elements.find((element) => element.id === key)!.kind as CreateInspectionRequestDTO['data'][number]['kind'],
        })),
      });

      return await response.json();
    },
    onError: () => {
      toast.error('Unable to save changes. Please try again.');
    },
    onSuccess: () => {
      toast.success('Form submitted successfully');
      queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(assetId) });
      onOpenChange?.(false, true);
    },
  });

  const { mutateAsync: createScheduleForm, isPending: isCreatingSchedule } = useMutation({
    mutationFn: async (data: FormRendererData) => {
      if (!form?.latestRevisionId) {
        throw new Error("Couldn't find the associated form revision");
      }

      const response = await AWAApiV2Service.CreateScheduleFormSubmission({
        assetId: assetId,
        formRevisionId: form.latestRevisionId,
        data: Object.entries(data).map(([key, value]) => ({
          elementId: key,
          value: value === undefined ? null : value,
          kind: form.elements.find((element) => element.id === key)!.kind as CreateInspectionRequestDTO['data'][number]['kind'],
        })),
      });

      return await response.json();
    },
    onError: () => {
      toast.error('Unable to save changes. Please try again.');
    },
    onSuccess: () => {
      toast.success('Form submitted successfully');
      queryClient.invalidateQueries({ queryKey: activityKeys.byAsset(assetId) });
      onOpenChange?.(false, true);
    },
  });

  const isSubmitting = isCreatingInspection || isCreatingRepair || isCreatingSchedule;

  const handleSubmitForm = useCallback(
    async (data: FormRendererData) => {
      match(form?.type)
        //https://github.com/gvergnaud/ts-pattern/issues/183
        .with(FormType.InspectionForm as 0, async () => await createInspectionForm(data))
        .with(FormType.ScheduleForm as 1, async () => await createScheduleForm(data))
        .with(FormType.RepairForm as 2, async () => await createRepairForm(data))
        .with(undefined, () => {})
        .otherwise(() => {});
    },
    [form, createInspectionForm, createScheduleForm, createRepairForm],
  );

  function handleError() {
    const formState = formInstance.formState;
    toast.error('The record has invalid or incomplete data. Please check.');
    const fieldNames = Object.keys(formState.errors);
    if (fieldNames.length > 0) {
      const firstFieldWithError = document.getElementById(fieldNames[0]);
      if (firstFieldWithError) {
        firstFieldWithError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    throw new Error('The record has invalid or incomplete data. Please check.');
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={'right'}
        className="mx-auto h-full w-full sm:max-w-[800px]"
        hideCloseIcon
        onInteractOutside={(e) => e.preventDefault()}
        onCloseAutoFocus={() => {
          setIsSheetAnimationComplete(false);
        }}
        onAnimationEnd={() => {
          if (open) {
            setIsSheetAnimationComplete(true);
          }
        }}>
        <SheetHeader className="border-grey-3 m-0 border-b-2 p-0">
          <div className="flex h-[80px] w-full flex-row items-center justify-between gap-2 bg-white px-4 py-5">
            <div
              className="mx-auto flex h-[30px] min-w-0 flex-1 cursor-pointer flex-row items-center gap-2 p-0"
              onClick={() => {
                if (isFormDirty) {
                  setIsConfirmSaveDirtyChangesOpen(true);
                } else {
                  onOpenChange?.(false);
                }
              }}>
              <ArrowLeftIcon size={30} className="shrink-0" />
              <div className="text-default-text line-clamp-2 text-xl font-medium break-words">Create {form?.name ?? 'Record'}</div>
            </div>
            <div className="shrink-0">
              <Button
                variant="link"
                className={`text-default-green text-xl font-medium`}
                onClick={() => {
                  formInstance
                    .handleSubmit(handleSubmitForm, handleError)()
                    .then(() => {
                      onOpenChange?.(false, true);
                    })
                    .catch(() => {
                      onOpenChange?.(true);
                    });
                }}
                disabled={assetLoading || formLoading || !isFormDirty}>
                Submit
              </Button>
            </div>
          </div>
        </SheetHeader>
        <div className="flex flex-col overflow-y-auto">
          {!isSheetAnimationComplete || assetLoading || formLoading ? (
            <div className="flex h-screen w-screen items-center justify-center">
              <div className="border-default-grey-2 border-t-default-blue size-[30px] animate-spin rounded-full border-4" />
            </div>
          ) : (
            <div className="flex h-full w-full flex-col">
              <div className="w-full px-6 pt-4 pb-6">
                <div className="relative flex h-full w-full flex-col">
                  {selectedAsset && form ? (
                    <FormProvider {...formInstance}>
                      <form className="flex flex-col gap-2">
                        <FormElementsRenderer asset={selectedAsset} form={form} readonly={false} elements={form.elements} />
                      </form>
                    </FormProvider>
                  ) : !selectedAsset ? (
                    <div className="text-secondary-text mt-4 flex h-full items-center justify-center">No asset found.</div>
                  ) : (
                    <div className="text-secondary-text flex h-full items-center justify-center">Error loading form data.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>

      <SaveChangesDialog
        open={!!open && isConfirmSaveDirtyChangesOpen}
        onOpenChange={(newOpen) => {
          if (!newOpen && isSubmitting) return;
          setIsConfirmSaveDirtyChangesOpen(newOpen);
        }}
        saveCallback={() => {
          formInstance
            .handleSubmit(handleSubmitForm, handleError)()
            .then(() => onOpenChange?.(false, true))
            .catch(() => onOpenChange?.(true));
          setIsConfirmSaveDirtyChangesOpen(false);
        }}
        discardCallback={() => {
          setIsConfirmSaveDirtyChangesOpen(false);
          onOpenChange?.(false);
        }}
        loading={isSubmitting}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </Sheet>
  );
}
